import {Group} from "./model/Group.ts";
import {DataLimitationValue} from "./model/DataLimitation.ts";
import {Role} from "./model/Application.ts";
import {useEffect} from "react";
import {UserAccountInfo} from "./model/UserAccountInfo.ts";

export function deepEqualWithoutIdAndDataLimitationId(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1).filter(key => key !== 'id' && key !== 'groupAppRoleDataLimitationId');
    const keys2 = Object.keys(obj2).filter(key => key !== 'id' && key !== 'groupAppRoleDataLimitationId');

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key)) {
            return false;
        }

        if (!deepEqualWithoutIdAndDataLimitationId(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

export const checkComplexityPassword = (password?: string) => {
    let countOfSpecialGroups = 0;
    if(password && password != "" && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        countOfSpecialGroups++
    }
    if(password && password != "" && /[0-9]/.test(password)) {
        countOfSpecialGroups++
    }
    if(password && password != "" && /[A-Z]/.test(password)) {
        countOfSpecialGroups++
    }
    if(password && password != "" && /[a-z]/.test(password)) {
        countOfSpecialGroups++
    }
    return countOfSpecialGroups >= 3
}

export const findRecursivelyGroupById = (groupId: number, groups: Group[]): Group | null => {
    if (groupId == null) return null;

    const rootGroupCheck = groups.find(g => g.id == groupId);
    if (rootGroupCheck) return rootGroupCheck;

    const rootGroup = groups.find(g =>
        g.subgroups.some(subgroup => findRecursivelyGroup(subgroup, groupId) != null)
    );
    if (rootGroup) {
        const filterSubgroupsRecursively = (subgroups: Group[]): Group[] => {
            return subgroups
                .map(subgroup => {
                    if(subgroup.id === groupId){
                        return subgroup
                    }
                    const filteredSubgroup = findRecursivelyGroup(subgroup, groupId);
                    if (filteredSubgroup) {
                        return {
                            ...subgroup,
                            subgroups: filterSubgroupsRecursively(subgroup.subgroups),
                        };
                    }
                    return null;
                })
                .filter(subgroup => subgroup != null) as Group[];
        };

        return {
            ...rootGroup,
            subgroups: filterSubgroupsRecursively(rootGroup.subgroups),
        };
    }

    return null;
}

export const findRecursivelyGroup = (group: Group | null, groupId: number): Group | null => {
    if(group == null) return null
    if(group.id == groupId) return group
    if(group.subgroups.length === 0) return null
    return group.subgroups.map(subgroup => findRecursivelyGroup(subgroup, groupId)).filter(group => !!group)[0]
}
const findRecursivelyGroupList = (group: Group | null, groupId: number): Group[] => {
    if(group == null) return []
    if(group.id == groupId) return [group]
    if(group.subgroups.length === 0) return []
    const subgroups = group.subgroups.map(subgroup => findRecursivelyGroupList(subgroup, groupId)).flat()
    return subgroups.length > 0 ? [group].concat(subgroups) : []
}

export const findRecursiveIfGroupIsManageable = (groups: Group[], groupId: number, accountInfo: UserAccountInfo, withItself: boolean = true) => {
    return groups.map(rootGroup => findRecursivelyGroupList(rootGroup, groupId)).filter(listGroups => listGroups && listGroups.length > 0).some(listGroups => listGroups.filter(g => withItself || g.id !== groupId).some(g => accountInfo.manager_of_group_ids.includes(g.id)))
}

export const findRecursivelyGroupMatch = (search: string, activeOnly: boolean, group: Group): boolean => {
    if(group.name.toLowerCase().includes(search.toLowerCase())) return true
    if(group.subgroups.length === 0) return false
    return group.subgroups.filter(sg => !activeOnly || sg.active).some(group => findRecursivelyGroupMatch(search, activeOnly, group))
}

export const findRecursiveDataLimitationValueMatch = (dlv: DataLimitationValue, search: string): boolean => {
    if(dlv.sub_values.length === 0){
        return dlv.key.toLowerCase().includes(search.toLowerCase()) || dlv.value.toLowerCase().includes(search.toLowerCase())
    }
    return dlv.sub_values.some(subValue => findRecursiveDataLimitationValueMatch(subValue, search))
}

export function invertAppRoleToApplications(data: Role[]) {
    return data.map(role => role.application).filter((value, index, self) => self.findIndex(app => app.id === value.id) === index).map(app => ({...app, roles: data.filter(role => role.application.id === app.id).map(moveRelationsToDataLimitation)}));
}

function moveRelationsToDataLimitation(role: Role)  {
    return {...role, data_limitations: role.data_limitations.map(dl => ({...dl, actual_related_data_limitations: role.data_limitation_relations.filter(rel => rel.data_limitation_id === dl.id).map(rel => rel.data_limitation_relation)}))}
}

export function onlyUnique<T>(value: T, index: number, array: T[]) {
    return array.indexOf(value) === index;
}

export const updateTreeOddEvenClasses = () => {
    // Select all visible tree nodes within the container
    const nodes = document.querySelectorAll(`.node-item`);

    // Iterate through the nodes and assign isEven or isOdd classes
    nodes.forEach((node, index) => {
        node.classList.remove('bg-white', 'bg-gray-50'); // Clear previous classes
        if (index % 2 === 0) {
            node.classList.add('bg-white'); // Even index
        } else {
            node.classList.add('bg-gray-50'); // Odd index
        }
    });
}

/**
 * https://stackoverflow.com/a/63359693
 * This Hook can be used for detecting clicks outside the Opened Menu
 */
export function useClickOutside(ref: any, onClickOutside: () => void) {
    useEffect(() => {
        /**
         * Invoke Function onClick outside of element
         */
        function handleClickOutside(event: any) {
            if (ref.current && !ref.current.contains(event.target)) {
                onClickOutside();
            }
        }
        // Bind
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // dispose
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, onClickOutside]);
}

export function noop() {
    // do nothing
}

export function isAdmin(accountInfo: UserAccountInfo): boolean {
    return !!(accountInfo.account !== null && accountInfo.roles && accountInfo.roles.length > 1)
}

export function isInvalidEmail(email: string): boolean {
    return !/^([^'"\/;:<>)(*^%$#!\s@À-ÿ\u0370-\u03FF\u0400-\u04FF]+(\.[^'"\/;:<>)(*^%$#!\s@À-ÿ\u0370-\u03FF\u0400-\u04FF]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
}

const INVALID_CHARACTERS_REGEXP = /['";:\/<>)(*^%$#@!]/
export const containsInvalidCharacters = (value: string) => {
    return INVALID_CHARACTERS_REGEXP.test(value);
};

const EMAIL_INVALID_CHARACTERS_REGEXP = /['";:\/<>)(*^%$#!]/
export const emailContainsInvalidCharacters = (value: string) => {
    return EMAIL_INVALID_CHARACTERS_REGEXP.test(value);
};

export function escapeCharacters(str: string) {
    let escapedStr = '';
    for (let i = 0; i < str.length; i++) {
        const char = str.charAt(i);
        switch (char) {
            case '#':
                escapedStr += "&#035;";
                break;
            case ';':
                escapedStr += ";&#059;";
                break;
            case "'":
                escapedStr += "&#039;";
                break;
            case '"':
                escapedStr += "&quot;";
                break;
            case ':':
                escapedStr += "&#058;";
                break;
            case '/':
                escapedStr += "&#047;";
                break;
            case '<':
                escapedStr += "&#060;";
                break;
            case '>':
                escapedStr += "&#062;";
                break;
            case ')':
                escapedStr += "&#041;";
                break;
            case '(':
                escapedStr += "&#040;";
                break;
            case '*':
                escapedStr += "&#042;";
                break;
            case '^':
                escapedStr += "&#094;";
                break;
            case '%':
                escapedStr += "&#037;";
                break;
            case '$':
                escapedStr += "&#036;";
                break;
            case '@':
                escapedStr += "&#064;";
                break;
            case '!':
                escapedStr += "&#033;";
                break;
            default:
                escapedStr += char;
        }
    }
    return escapedStr;
}